/**
 * CampaignStepGrid
 */

import {
  OmniCardElement,
  OmniTileElement,
  OmniToolbarElement,
  OmniTooltipElement,
  OmniIconElement,
  OmniElement,
  OmniStyleElement,
  classMap,
  html,
  css,
} from 'omni-ui';

import { selectSteps } from '../helpers/selectors.js';
import { pixToRem, wait, trackPortalEvent } from '../helpers/utils.js';

import './campaign-step-detail.js';

OmniCardElement.register();
OmniTileElement.register();
OmniToolbarElement.register();
OmniTooltipElement.register();
OmniIconElement.register();
OmniStyleElement.register();

export class CampaignStepGrid extends OmniElement {
  static get properties() {
    return {
      activeStageId: { type: String },
      data: { type: Object },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          --omni-card-height: ${pixToRem(160)};
          --omni-card-min-width: ${pixToRem(300)};
          --omni-card-max-width: auto;
          --omni-tile-background-color: rgb(var(--rgb-core-light));
          min-height: 100%;
        }
        :host,
        omni-style {
          flex-grow: 1;
          display: flex;
          flex-direction: column;
        }
        omni-card::part(card-img),
        omni-card::part(card-icon) {
          display: none;
        }
        omni-card::part(card-footer) {
          border-top: 0.14rem solid rgb(var(--rgb-core-medium-two));
          padding: 0.29rem 0.43rem;
        }
        omni-tile {
          flex-grow: 1;
        }
        .step,
        .step-list,
        .step-list > * {
          display: flex !important; /* override .omni .box { display: block } */
          flex-direction: column;
          flex-grow: 1;
          /* TODO look into better ways to resolve the card width */
          flex-basis: 0;
        }
        .depth-0 > .step-list {
          flex-direction: row;
        }
        .depth-0 > .step-content {
          display: none;
        }
        .step-list.depth-1 > li + li {
          margin-left: 1.71rem;
        }
        .step-list.depth-1 > li omni-toolbar {
          --omni-toolbar-padding: 1.43rem;
        }
        .step-list.depth-1 > li omni-toolbar::part(toolbar) {
          min-height: 3rem;
        }
        .step-list.depth-1 > li omni-toolbar .title {
          padding-bottom: 0.36rem;
          min-width: 6.43rem;
        }
        .step-list.depth-2 {
          margin-top: 0.36rem;
        }
        .step-list.depth-1 > li:nth-child(4n + 1) omni-toolbar .title {
          box-shadow: 0 0.21rem 0 0 rgb(var(--rgb-accent-one));
        }
        .step-list.depth-1 > li:nth-child(4n + 2) omni-toolbar .title {
          box-shadow: 0 0.21rem 0 0 rgb(var(--rgb-accent-two));
        }
        .step-list.depth-1 > li:nth-child(4n + 3) omni-toolbar .title {
          box-shadow: 0 0.21rem 0 0 rgb(var(--rgb-accent-three));
        }
        .step-list.depth-1 > li:nth-child(4n + 4) omni-toolbar .title {
          box-shadow: 0 0.21rem 0 0 rgb(var(--rgb-accent-four));
        }
        .step-list.depth-2 > li + li {
          margin-top: 0.86rem;
        }
        omni-card::part(card-footer) {
          justify-content: space-evenly;
          align-content: center;
          align-items: center;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.data = null;
    this.modalOpen = false;
  }

  firstUpdated() {
    if (!this.activeStageId) this._setActiveStage();
  }

  /**
   * Sets active stage in accordion overview
   * @param {Object} stage - Currently active stage
   */
  _setActiveStage = async (
    id = this.data?.framework_template.steps[0].uuid_step,
  ) => {
    /**
     * Avoid potentially dispatching a stage change during a render-cycle
     * with an undefined campaignId when Portal changes the route away from campaign.
     * The route change makes the campaignId route param unavailable to campaign components.
     */
    await wait();
    this.dispatchNewEvent('active-stage-change', {
      detail: { id },
    });
  };

  /**
   * @memberof utils
   * @function
   * @param {Object} _step - **Unused** step object
   * @param {number} depth - Indicates depth of list item for classname generation
   * @returns {Object}
   */
  _getDepthClasses = (className, depth, active = false) => ({
    [className]: true,
    [`depth-${depth}`]: true,
    active,
  });

  _getListTmpl(list, depth = 0) {
    if (!list || !list.length) return '';

    return html`
      <ol
        class="m-0 p-0 ${classMap(this._getDepthClasses('step-list', depth))}"
      >
        ${list.map(step => html`<li>${this._getStepTmpl(step, depth)}</li>`)}
      </ol>
    `;
  }

  // decision group = stage
  _getDecisionGroupTmpl(step, depth) {
    return html`
      <omni-tile
        class=${classMap(
          this._getDepthClasses(
            'step',
            depth,
            this.activeStageId === step.uuid_step,
          ),
        )}
      >
        <omni-toolbar slot="subheader">
          <h1 class="title is-3">${step.name}</h1>
        </omni-toolbar>
        ${this._getListTmpl(selectSteps(step), depth + 1)}
      </omni-tile>
    `;
  }

  // decision = step
  _getDecisionTmpl(step, depth) {
    return html`
      <omni-card
        href="javascript: void(0);"
        class=${classMap(this._getDepthClasses('step', depth))}
        openmsg="Open"
        @open=${e => {
          e.preventDefault();
          trackPortalEvent('step', 'open', 'campaign-landing');
          this._setActiveStep(step);
        }}
      >
        <h1 slot="card-title" class="title is-5 mb-2">${step.name}</h1>
        <h2 slot="card-type" class="mt-1">${step.display_order_label}</h2>
        <p slot="card-desc">${step.description}</p>
        <div slot="card-status">
          <campaign-step-status
            class="ml-2"
            .stepId=${step.uuid_step}
            .campaign=${this.data}
            readonly
          ></campaign-step-status>
        </div>
        <!-- <div slot="card-menu" class="dropdown-content">
          <a href class="dropdown-item">Action Item</a>
          <a href class="dropdown-item">Action Item</a>
        </div> -->
      </omni-card>
    `;
  }

  // step is either a stage or step
  _getStepTmpl(step, depth) {
    switch (depth) {
      case 0: // Campaign
        return html`
          <article class=${classMap(this._getDepthClasses('step', depth))}>
            <div class="step-content">
              <h1 class="step-name">${step.name}</h1>
              <p class="step-description">${step.description}</p>
            </div>
            ${this._getListTmpl(selectSteps(step), depth + 1)}
          </article>
        `;

      case 1: // Decision Group
        return this._getDecisionGroupTmpl(step, depth);

      case 2: // Decision
        return this._getDecisionTmpl(step, depth);

      default:
        return '';
    }
  }

  /**
   * Emits event to open invidual step view
   * @event CampaignAccordion~activestepchange
   * @type {object}
   * @param {Object} step - Step clicked by user to be displayed (made active)
   */
  _setActiveStep(step) {
    this.dispatchNewEvent(
      'active-step-change',
      // empty value string -> null
      { detail: { id: step?.uuid_step || null, parent_id: step?.parent_id } },
    );
  }

  render() {
    const stage = this.data ? [this.data] : null;

    return html` <omni-style> ${this._getListTmpl(stage)} </omni-style> `;
  }
}

OmniElement.register('campaign-step-grid', CampaignStepGrid);
